import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "auction-table" }
const _hoisted_2 = ["onClick", "textContent"]
const _hoisted_3 = {
  key: 0,
  class: "click-indicator"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { style: {"white-space":"nowrap"} }
const _hoisted_6 = { style: {"font-size":"12px","white-space":"nowrap"} }
const _hoisted_7 = { style: {"font-size":"12px"} }
const _hoisted_8 = {
  key: 0,
  class: "strategy-history"
}
const _hoisted_9 = { class: "strategy-history__caption" }
const _hoisted_10 = {
  key: 1,
  class: "strategy-history__caption"
}
const _hoisted_11 = { class: "strategies-container" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import { formatPrice } from '@/common/function'
import { getNameArtFromId } from '@/common/list'
import { useWebSocketStore } from '@/store/websocket.store'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'
import {
  ArtefactScoreDetails,
  calculateArtefactScore,
  artefactScoreTooltip
} from '@/common/artefact-score'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  passesMultiLotStrategyFilters
} from '@/common/artefact-filters'

interface CurrentLot {
  price: number
  startTime: string
  endTime: string
  currentPrice: number
  additionalInfo: object | null
}

interface MultiLotStrategy {
  lotsCount: number
  totalCost: number
  totalSalePrice: number
  totalCommission: number
  totalProfit: number
  averageLotPrice: number
  profitPerLot: number
  lots: CurrentLot[]
  salePricePerLot: number
}

export interface TableRow {
  qlt: number
  ptn: string
  averagePrice: string | null
  historyMinPrice: number
  historySalePrices: HistoryLot[]
  currentPrice: number
  currentElems: CurrentLot[]
  profit: string | null
  commission: number | null
  salePrice: number | null
  startPrice: number | null
  startTime: string | null
  endTime: string | null
  isBid?: boolean
  additionalInfo: object | null
  multiLotStrategies: MultiLotStrategy[]
  historyCount?: number
  sales24h: number
  sales48h: number
}

interface StrategyTableRow extends TableRow {
  id: string
  name: string
  score: number
  scoreDetails: ArtefactScoreDetails
  multiLotStrategies: ScoredMultiLotStrategy[]
}

interface ScoredMultiLotStrategy extends MultiLotStrategy {
  score: number
  scoreDetails: ArtefactScoreDetails
}

interface StrategyPayload {
  multiLotStrategies: Record<string, { name?: string; rows?: TableRow[] }>
  artefactProfitMap?: Record<string, { rows?: TableRow[] }>
}

interface HistoryLot {
  price: number
  time: string | Date
  additional: object | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ArtefactDashboardStrategies',
  props: {
    onlyGoodLots: { type: Boolean, default: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid },
    liquidityWindow: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow },
    minLiquiditySales: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales },
    minBuyoutProfit: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit }
  },
  setup(__props: any) {

const props = __props

const toast = ref()

const wsStore = useWebSocketStore()

const tableData = ref<StrategyTableRow[]>([])

// const expandedRows = ref<Record<string, boolean>>({});

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

function getStrategyHistory(
  artefactId: string,
  row: TableRow,
  data: StrategyPayload
): HistoryLot[] {
  if (row.historySalePrices?.length) return row.historySalePrices

  const matchesGroup = (candidate: TableRow) =>
    !candidate.isBid &&
    candidate.qlt === row.qlt &&
    candidate.ptn === row.ptn &&
    candidate.historySalePrices?.length > 0

  const historyRow =
    data.artefactProfitMap?.[artefactId]?.rows?.find(matchesGroup) ??
    wsStore.artefactAllMap[artefactId]?.rows?.find(matchesGroup)

  return historyRow?.historySalePrices ?? []
}

function onArtefactProfitAll(data: StrategyPayload) {
  const next: StrategyTableRow[] = []
  const now = Date.now()

  for (const artefactId in data?.multiLotStrategies ?? {}) {
    const artefact = data.multiLotStrategies[artefactId]
    if (!artefact?.rows) continue

    const name = artefact.name || getNameArtFromId(artefactId)

    artefact.rows.forEach((row: TableRow) => {
      const strategies = (row.multiLotStrategies ?? [])
        .filter(strategy => passesMultiLotStrategyFilters(row, strategy, filterSettings.value, now))
        .map(strategy => {
          const scoreDetails = calculateArtefactScore(
            {
              ...row,
              rawProfit: strategy.totalProfit,
              currentPrice: strategy.totalCost,
              lotsCount: strategy.lotsCount
            },
            props.liquidityWindow,
            now
          )
          return { ...strategy, score: scoreDetails.score, scoreDetails }
        })
        .sort((a, b) => b.score - a.score || b.totalProfit - a.totalProfit)

      if (strategies.length === 0 || row.isBid) return

      const item = {
        ...row,
        historySalePrices: getStrategyHistory(artefactId, row, data),
        multiLotStrategies: strategies,
        score: strategies[0].score,
        scoreDetails: strategies[0].scoreDetails,
        name,
        id: `${artefactId}_${row.qlt}_${row.ptn}`
      }

      next.push(item)
    })
  }

  next.sort((a, b) => {
    return (
      b.score - a.score ||
      b.multiLotStrategies[0].totalProfit - a.multiLotStrategies[0].totalProfit ||
      b.sales48h - a.sales48h ||
      a.id.localeCompare(b.id)
    )
  })

  tableData.value = next
}

const filterSettings = computed<ArtefactFilterSettings>(() => ({
  onlyLiquid: props.onlyGoodLots,
  liquidityWindow: props.liquidityWindow,
  minSales: props.minLiquiditySales,
  minProfit: props.minBuyoutProfit
}))

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string, type: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })

  wsStore.sendArt({
    text,
    type
  })
}

function getAuctionTimer(startIso: string, endIso: string) {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const now = Date.now()

  // const duration = end - start;
  const passed = Math.max(0, now - start)
  const left = Math.max(0, end - now)

  return {
    passedMs: passed,
    leftMs: left
  }
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)

  if (h > 0) return `${h}ч ${m % 60}м`
  if (m > 0) return `${m}м ${s % 60}с`
  return `${s}с`
}

const highlightMap = ref<Record<string, { adminId: number; until: number }>>({})

const highlightMapPrice = ref<Record<string, { adminId: number; until: number }>>({})

function onAdminClick(data) {
  if (data.type === 'name') {
    const item = tableData.value.find(i => i.name === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMap.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMap.value[item.id]
    }, duration)
  } else if (data.type === 'price') {
    const item = tableData.value.find(i => i.salePrice === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMapPrice.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMapPrice.value[item.id]
    }, duration)
  }
}

watch(
  [
    () => wsStore.artefactProfitData,
    () => wsStore.artefactAllMap,
    () => props.onlyGoodLots,
    () => props.liquidityWindow,
    () => props.minLiquiditySales,
    () => props.minBuyoutProfit
  ],
  () => {
    onArtefactProfitAll(wsStore.artefactProfitData)
  },
  { immediate: true }
)

onMounted(() => {
  wsStore.connect() // 👈 ВАЖНО

  wsStore.artefactSocket?.on('admin_click', onAdminClick)
})

onBeforeUnmount(() => {
  wsStore.artefactSocket?.off('admin_click', onAdminClick)
  tableData.value = []
})

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Toast), {
      ref_key: "toast",
      ref: toast
    }, null, 512),
    _createVNode(_unref(DataTable), {
      value: tableData.value,
      dataKey: "id",
      responsiveLayout: "scroll",
      sortField: "score",
      sortOrder: -1,
      emptyMessage: "Подходящих стратегий сейчас нет"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          field: "name",
          header: "Артефакт"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", {
              class: _normalizeClass({
              'row-highlight': highlightMap.value[data.id]
            }),
              style: {"white-space":"nowrap","position":"relative"}
            }, [
              _createElementVNode("span", {
                onClick: ($event: any) => (copyToClipboard(data.name, 'name')),
                textContent: _toDisplayString(data.name)
              }, null, 8, _hoisted_2),
              _cache[0] || (_cache[0] = _createTextVNode("  ", -1)),
              _createElementVNode("span", {
                style: _normalizeStyle([{"white-space":"nowrap"}, `color: ${getColorQlt(data.qlt)}`])
              }, _toDisplayString(data.additionalInfo?.ptn ?? data.ptn), 5),
              (highlightMap.value[data.id])
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, " 👤 " + _toDisplayString(highlightMap.value[data.id].adminId), 1))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "score",
          header: "Score",
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _withDirectives((_openBlock(), _createElementBlock("span", null, [
              _createTextVNode(_toDisplayString(data.score.toFixed(1)), 1)
            ])), [
              [
                _directive_tooltip,
                _unref(artefactScoreTooltip)(data.scoreDetails),
                void 0,
                { bottom: true }
              ]
            ])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "averagePrice",
          header: "Медиана"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(formatPrice)(Number(data.averagePrice))), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentElems",
          header: "Лоты"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.currentElems, (lot, index) => {
                return (_openBlock(), _createElementBlock("span", { key: index }, [
                  _createElementVNode("span", null, _toDisplayString(_unref(formatPrice)(lot.price)), 1),
                  (lot.additionalInfo?.ptn !== undefined)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[1] || (_cache[1] = _createTextVNode(" - ", -1)),
                        _createElementVNode("span", null, _toDisplayString(lot.additionalInfo.ptn), 1)
                      ], 64))
                    : _createCommentVNode("", true),
                  _cache[2] || (_cache[2] = _createTextVNode("; ", -1))
                ]))
              }), 128))
            ])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentElems",
          header: "Время"
        }, {
          body: _withCtx(({ data }) => [
            (data.currentElems.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, _toDisplayString(formatMs(getAuctionTimer(data.startTime, data.endTime).passedMs)), 1),
                  _createElementVNode("div", _hoisted_6, _toDisplayString(formatMs(getAuctionTimer(data.startTime, data.endTime).leftMs)), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), { header: "Продажи" }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(data.sales24h) + " / 24ч", 1),
            _createElementVNode("div", _hoisted_7, _toDisplayString(data.sales48h) + " / 48ч", 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "historySalePrices",
          header: "Количество проданных"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(data.historyCount ?? data.historySalePrices.length), 1),
            (data.historySalePrices.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(HistoryChart, {
                    historySalePrices: data.historySalePrices,
                    "sale-price": data.multiLotStrategies[0].salePricePerLot
                  }, null, 8, ["historySalePrices", "sale-price"]),
                  _createElementVNode("div", _hoisted_9, " Линия: " + _toDisplayString(_unref(formatPrice)(data.multiLotStrategies[0].salePricePerLot)) + " · лучшая по score, " + _toDisplayString(data.multiLotStrategies[0].lotsCount) + " шт. ", 1)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_10, "История загружается…"))
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), { header: "Стратегии" }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.multiLotStrategies, (strategy, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "strategy-card"
                }, [
                  _createElementVNode("div", {
                    class: "strategy-header",
                    onClick: ($event: any) => (copyToClipboard(strategy.salePricePerLot.toFixed(), 'price'))
                  }, [
                    _createElementVNode("strong", null, _toDisplayString(strategy.lotsCount) + " шт.", 1),
                    _createElementVNode("span", {
                      style: _normalizeStyle(`color: ${strategy.totalProfit > 0 ? 'green' : 'red'}`)
                    }, _toDisplayString(_unref(formatPrice)(strategy.totalProfit)), 5)
                  ], 8, _hoisted_12),
                  _createElementVNode("div", {
                    class: "strategy-details",
                    onClick: ($event: any) => (copyToClipboard(strategy.salePricePerLot.toFixed(), 'price'))
                  }, [
                    _createElementVNode("div", null, "Стоимость выкупа: " + _toDisplayString(_unref(formatPrice)(strategy.totalCost)), 1),
                    _withDirectives((_openBlock(), _createElementBlock("div", null, [
                      _createTextVNode(" Score: " + _toDisplayString(strategy.score.toFixed(1)), 1)
                    ])), [
                      [
                        _directive_tooltip,
                        _unref(artefactScoreTooltip)(strategy.scoreDetails),
                        void 0,
                        { bottom: true }
                      ]
                    ]),
                    _createElementVNode("div", null, "Продажа: " + _toDisplayString(_unref(formatPrice)(strategy.totalSalePrice)), 1),
                    _createElementVNode("div", null, "Сред. цена лота: " + _toDisplayString(_unref(formatPrice)(strategy.averageLotPrice)), 1),
                    _createElementVNode("div", null, "Прибыль/лот: " + _toDisplayString(_unref(formatPrice)(strategy.profitPerLot)), 1),
                    _createElementVNode("div", null, "Цена продажи: " + _toDisplayString(_unref(formatPrice)(strategy.salePricePerLot.toFixed())), 1)
                  ], 8, _hoisted_13),
                  _createElementVNode("div", {
                    class: "lots-prices",
                    onClick: ($event: any) => (copyToClipboard(strategy.salePricePerLot.toFixed(), 'price'))
                  }, [
                    _createElementVNode("small", null, "Цены лотов: " + _toDisplayString(strategy.lots.map(l => _unref(formatPrice)(l.price)).join(', ')), 1)
                  ], 8, _hoisted_14)
                ]))
              }), 128))
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["value"])
  ]))
}
}

})